import { useState, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import authService from '../api/authService';
import { Button, Input, Alert } from '@/shared/components/ui';
import { MENSAJES_ERROR, MENSAJES_EXITO, TEXTOS_UI } from '../constants';
import { useSecretoDeUrl } from '../hooks/useSecretoDeUrl';

export const RestablecerContrasenaForm = () => {
    // El token se lee una vez (fragmento, ruta o query) y la URL se limpia: los efectos dependen del estado, no de la URL.
    const { token: tokenDeRuta } = useParams<{ token: string }>();
    const token = useSecretoDeUrl('token', tokenDeRuta);
    const [nuevaContrasena, setNuevaContrasena] = useState('');
    const [confirmarContrasena, setConfirmarContrasena] = useState('');
    const [tokenValido, setTokenValido] = useState<boolean | null>(null);
    const [estaCargando, setEstaCargando] = useState(false);
    const [error, setError] = useState('');
    const [exitoso, setExitoso] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const verificarToken = async () => {
            if (!token) {
                setTokenValido(false);
                return;
            }

            try {
                const esValido = await authService.validarToken(token);
                setTokenValido(esValido);
            } catch {
                setTokenValido(false);
            }
        };

        verificarToken();
    }, [token]);

    const manejarEnvio = async (e: React.FormEvent) => {
        e.preventDefault();

        if (nuevaContrasena !== confirmarContrasena) {
            setError(MENSAJES_ERROR.PASSWORD_NO_COINCIDEN);
            return;
        }

        setEstaCargando(true);
        setError('');

        try {
            await authService.restablecerContrasena(token!, nuevaContrasena, confirmarContrasena);
            setExitoso(true);
            setTimeout(() => navigate('/login'), 3000);
        } catch (err: any) {
            // Prioridad 1: Mensaje directo del backend (DomainExceptions)
            if (err.response?.data?.message) {
                setError(err.response.data.message);
            }
            // Prioridad 2: Errores de validación (FluentValidation)
            else if (err.response?.data?.errors) {
                const errorsObj = err.response.data.errors;
                const allMessages: string[] = [];

                Object.keys(errorsObj).forEach(field => {
                    if (Array.isArray(errorsObj[field])) {
                        allMessages.push(...errorsObj[field]);
                    }
                });

                setError(allMessages.join('. '));
            }
            // Prioridad 3: Título (fallback)
            else if (err.response?.data?.title) {
                setError(err.response.data.title);
            }
            // Prioridad 4: Mensaje genérico
            else {
                setError(MENSAJES_ERROR.ERROR_RESTABLECER);
            }
        } finally {
            setEstaCargando(false);
        }
    };

    if (tokenValido === null) {
        return (
            <div className="login-form form-centered">
                <div className="loading-spinner" />
                <p>{TEXTOS_UI.CARGANDO}</p>
            </div>
        );
    }

    if (!tokenValido) {
        return (
            <form className="login-form form-centered">
                <Alert type="error">{MENSAJES_ERROR.TOKEN_INVALIDO}</Alert>
                <Button
                    type="button"
                    onClick={() => navigate('/recuperar-contrasena')}
                    variant="primary"
                    fullWidth
                    className="login-btn"
                >
                    {TEXTOS_UI.RECUPERACION_TITULO}
                </Button>
            </form>
        );
    }

    if (exitoso) {
        return (
            <form className="login-form form-centered">
                <Alert type="success">{MENSAJES_EXITO.PASSWORD_CAMBIADO}</Alert>
            </form>
        );
    }

    return (
        <form className="login-form" onSubmit={manejarEnvio}>
            <Input
                id="nuevaContrasena"
                type="password"
                label={TEXTOS_UI.RESTABLECER_NUEVA_PASSWORD_LABEL}
                value={nuevaContrasena}
                onChange={(e) => setNuevaContrasena(e.target.value)}
                placeholder={TEXTOS_UI.RESTABLECER_NUEVA_PASSWORD_PLACEHOLDER}
                className="form-input"
                required
                disabled={estaCargando}
                autoFocus
                showPasswordToggle
                noMargin
            />

            <Input
                id="confirmarContrasena"
                type="password"
                label={TEXTOS_UI.RESTABLECER_CONFIRMAR_PASSWORD_LABEL}
                value={confirmarContrasena}
                onChange={(e) => setConfirmarContrasena(e.target.value)}
                placeholder={TEXTOS_UI.RESTABLECER_CONFIRMAR_PASSWORD_PLACEHOLDER}
                className="form-input"
                required
                disabled={estaCargando}
                showPasswordToggle
                noMargin
            />

            {error && <Alert type="error">{error}</Alert>}

            <Button
                type="submit"
                variant="primary"
                fullWidth
                disabled={estaCargando}
                loading={estaCargando}
                className="login-btn"
            >
                {TEXTOS_UI.RESTABLECER_BOTON}
            </Button>

            <button
                type="button"
                onClick={() => navigate('/login')}
                className="back-btn"
            >
                <ChevronLeft size={16} />
                {TEXTOS_UI.LOGIN_REGRESAR_BOTON}
            </button>
        </form>
    );
};
